import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Download } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const FaContext = createContext(null);
export const useFa = () => useContext(FaContext);

export const rp = (p) => {
  const n = Number(p || 0) / 100;
  return `${n < 0 ? "-" : ""}₹${Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
export const err = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Something went wrong");
export const inputCls = "h-9 w-full rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-900 focus:border-zinc-400 focus:outline-none";

export function useLoad(url, deps = []) {
  const [data, setData] = useState(null);
  const reload = useCallback(() => (url ? api.get(url).then((r) => setData(r.data)).catch(err) : Promise.resolve()), [url]); // eslint-disable-line
  useEffect(() => { reload(); }, [reload, ...deps]); // eslint-disable-line
  return [data, reload];
}

const TONE = {
  Paid: "bg-emerald-50 text-emerald-700", Approved: "bg-emerald-50 text-emerald-700", Available: "bg-emerald-50 text-emerald-700", Sent: "bg-emerald-50 text-emerald-700",
  "On Official Event Duty": "bg-emerald-50 text-emerald-700", Rejected: "bg-rose-50 text-rose-700", Overdue: "bg-rose-50 text-rose-700", Failed: "bg-rose-50 text-rose-700",
  Revoked: "bg-rose-50 text-rose-700", Cancelled: "bg-zinc-100 text-zinc-500", Reversed: "bg-zinc-100 text-zinc-500", Draft: "bg-zinc-100 text-zinc-600",
  "Awaiting Approval": "bg-amber-50 text-amber-700", "Payment Initiated": "bg-blue-50 text-blue-700", "Partially Paid": "bg-blue-50 text-blue-700",
  Proposed: "bg-amber-50 text-amber-700", Requested: "bg-amber-50 text-amber-700", Scheduled: "bg-blue-50 text-blue-700", "Manager Approved": "bg-amber-50 text-amber-700",
};
export const Pill = ({ v, testid }) => <span data-testid={testid} className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-bold ${TONE[v] || "bg-[#ECE2D4] text-[#5c4b36]"}`}>{v}</span>;

export function Metric({ label, value, tone = "", testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-[#D6C6B0] bg-white/70 p-4 transition-transform hover:-translate-y-0.5">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`mt-1.5 text-lg font-extrabold tabular-nums ${tone || "text-zinc-900"}`}>{value}</div>
    </div>
  );
}

export function Table({ cols, rows, testid, onRow, empty = "No records yet." }) {
  if (!rows?.length) return <div data-testid={`${testid}-empty`} className="rounded-xl border border-dashed border-[#D6C6B0] p-8 text-center text-sm text-zinc-500">{empty}</div>;
  return (
    <div className="thin-scroll overflow-x-auto rounded-xl border border-[#D6C6B0] bg-white/80" data-testid={testid}>
      <table className="w-full text-sm">
        <thead><tr className="border-b border-[#D6C6B0] bg-[#F3ECE2] text-left">{cols.map((c) => <th key={c.k} className={`whitespace-nowrap px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-zinc-600 ${c.right ? "text-right" : ""}`}>{c.l}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} data-testid={`${testid}-row-${i}`} onClick={onRow ? () => onRow(r) : undefined} className={`border-b border-zinc-100 last:border-0 ${onRow ? "cursor-pointer hover:bg-[#F8F4EE]" : ""}`}>
              {cols.map((c) => <td key={c.k} className={`px-4 py-2.5 align-middle text-zinc-700 ${c.right ? "text-right tabular-nums" : ""}`}>{c.r ? c.r(r) : r[c.k] || "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Pager({ total, skip, setSkip, size = 50, testid = "pager" }) {
  if (total <= size) return null;
  return (
    <div className="flex items-center justify-end gap-2 text-xs text-zinc-500" data-testid={testid}>
      <span>{skip + 1}–{Math.min(skip + size, total)} of {total}</span>
      <Button size="sm" variant="outline" disabled={!skip} onClick={() => setSkip(Math.max(skip - size, 0))} data-testid={`${testid}-prev`}>Prev</Button>
      <Button size="sm" variant="outline" disabled={skip + size >= total} onClick={() => setSkip(skip + size)} data-testid={`${testid}-next`}>Next</Button>
    </div>
  );
}

export async function download(url, name) {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = name;
    a.click();
  } catch (e) {
    const t = e?.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : null;
    toast.error(t || "Download failed");
  }
}

export function Files({ files = [], entityType, entityId, canAdd, onDone, testid }) {
  const add = async (e) => {
    for (const f of e.target.files) {
      const fd = new FormData();
      fd.append("entity_type", entityType); fd.append("entity_id", entityId); fd.append("file", f);
      await api.post("/fa/files", fd).then(() => toast.success(`${f.name} attached`)).catch(err);
    }
    e.target.value = "";
    onDone?.();
  };
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {files.map((f) => (
        <button key={f.id} data-testid={`${testid}-file-${f.id}`} onClick={() => download(`/fa/files/${f.id}`, f.name)} className="flex w-full items-center gap-2 rounded-md bg-[#F3ECE2] px-2.5 py-1.5 text-left text-xs text-zinc-700 hover:bg-[#ECE2D4]">
          <Paperclip className="h-3.5 w-3.5" /><span className="flex-1 truncate">{f.name}</span><Download className="h-3.5 w-3.5" />
        </button>
      ))}
      {!files.length && <div className="text-xs text-zinc-400">No documents attached</div>}
      {canAdd && <input data-testid={`${testid}-input`} type="file" multiple accept=".pdf,.jpg,.jpeg,.png" onChange={add} className="block text-xs file:mr-2 file:rounded-md file:border-0 file:bg-[#8B7356] file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white" />}
    </div>
  );
}

export const Field = ({ label, children, full, req }) => (
  <label className={`space-y-1 text-xs font-semibold text-zinc-600 ${full ? "sm:col-span-2" : ""}`}><span>{label}{req && <span className="text-rose-500"> *</span>}</span>{children}</label>
);

export function Sel({ value, onChange, options, testid, placeholder = "Select…" }) {
  return (
    <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls}>
      <option value="">{placeholder}</option>
      {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
    </select>
  );
}

export const History = ({ items = [] }) => (
  <ol className="space-y-2 border-l border-[#D6C6B0] pl-4" data-testid="history-list">
    {[...items].reverse().map((h, i) => (
      <li key={i} className="text-xs"><div className="font-semibold text-zinc-800">{h.action}</div><div className="text-zinc-500">{h.by} · {new Date(h.at).toLocaleString("en-IN")}</div>{h.comment && <div className="mt-0.5 text-zinc-600">“{h.comment}”</div>}</li>
    ))}
  </ol>
);
